import { useEffect, useState } from "react";

/**
 * Where the assistant opens (ticket 21.4).
 *
 * A floating window needs a page beside it to be worth anything, so it is used
 * only where there is room for both: at least `sm` wide (Tailwind's 640 px, the
 * same breakpoint the launcher's label uses) **and** tall enough that the window
 * is not a 300 px slot. Anywhere else the assistant opens as the full-screen
 * sheet, where being modal is right — there is no page beside it to use.
 *
 * The decision reads `innerWidth`/`innerHeight` rather than `matchMedia`: jsdom
 * has no `matchMedia`, and the two numbers are what the rule is about anyway.
 */
export type AssistantLayout = "window" | "sheet";

export const WINDOW_MIN_WIDTH = 640;
export const WINDOW_MIN_HEIGHT = 520;

export function layoutFor(width: number, height: number): AssistantLayout {
  return width >= WINDOW_MIN_WIDTH && height >= WINDOW_MIN_HEIGHT ? "window" : "sheet";
}

function currentLayout(): AssistantLayout {
  if (typeof window === "undefined") return "sheet";
  return layoutFor(window.innerWidth, window.innerHeight);
}

/** Follows the viewport live. The widget closes the assistant when this
 *  changes while it is open — see `AssistantWidget`. */
export function useAssistantLayout(): AssistantLayout {
  const [layout, setLayout] = useState<AssistantLayout>(currentLayout);

  useEffect(() => {
    const handleResize = () => setLayout(currentLayout());
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return layout;
}

/**
 * A touch-first device, where Enter in the composer inserts a newline and only
 * the button sends — the platform convention, and the only way to type a second
 * line on a phone keyboard. Guarded: jsdom and some embedded browsers have no
 * `matchMedia`, and a missing convenience must never take the composer down.
 */
export function isCoarsePointer(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(pointer: coarse)").matches;
}
